'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

type InstallChoice = { outcome: 'accepted' | 'dismissed' };

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<InstallChoice>;
}

type NavigatorWithStandalone = Navigator & { standalone?: boolean };

export function isIosDevice(navigatorValue: Pick<Navigator, 'maxTouchPoints' | 'platform' | 'userAgent'>) {
  return /iPhone|iPad|iPod/i.test(navigatorValue.userAgent)
    || (navigatorValue.platform === 'MacIntel' && navigatorValue.maxTouchPoints > 1);
}

export function isStandaloneDisplay() {
  const navigatorValue = window.navigator as NavigatorWithStandalone;
  return window.matchMedia('(display-mode: standalone)').matches
    || navigatorValue.standalone === true;
}

export function InstallApp() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isIos, setIsIos] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    if (isStandaloneDisplay()) return;

    const ios = isIosDevice(window.navigator);
    setIsIos(ios);
    setIsVisible(ios);

    const captureInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
      setIsVisible(true);
    };

    window.addEventListener('beforeinstallprompt', captureInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', captureInstallPrompt);
  }, []);

  useEffect(() => {
    if (!showInstructions) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowInstructions(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [showInstructions]);

  if (!isVisible) return null;

  async function install() {
    if (!installPrompt) {
      setShowInstructions(true);
      return;
    }

    const choice = await installPrompt.prompt();
    setInstallPrompt(null);
    if (choice.outcome === 'accepted') setIsVisible(false);
  }

  return (
    <>
      <aside className="install-app">
        <div>
          <strong>{isIos ? 'Use the full-screen app' : 'Keep Concept handy'}</strong>
          <span>{isIos ? 'Add it to your Home Screen for an edge-to-edge background.' : 'Install it on this device.'}</span>
        </div>
        <button onClick={install} type="button">
          Install app
        </button>
      </aside>

      {showInstructions && createPortal((
        <div
          aria-labelledby="install-guide-title"
          aria-modal="true"
          className="install-guide-backdrop"
          onPointerDown={(event) => {
            if (event.currentTarget === event.target) setShowInstructions(false);
          }}
          role="dialog"
        >
          <section className="install-guide">
            <header>
              <div>
                <span>Install on iPhone or iPad</span>
                <strong id="install-guide-title">Add Concept to your Home Screen</strong>
              </div>
              <button aria-label="Close install instructions" onClick={() => setShowInstructions(false)} type="button">
                <span aria-hidden="true">&times;</span>
              </button>
            </header>
            <ol>
              <li>
                <b aria-hidden="true">1</b>
                <span>Tap the <strong>Share</strong> button in your browser.</span>
                <svg aria-hidden="true" viewBox="0 0 24 24">
                  <path d="M12 16V3m0 0L7.5 7.5M12 3l4.5 4.5M5 11v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-8" />
                </svg>
              </li>
              <li>
                <b aria-hidden="true">2</b>
                <span>Choose <strong>Add to Home Screen</strong>. You may need to scroll down.</span>
              </li>
              <li>
                <b aria-hidden="true">3</b>
                <span>Tap <strong>Add</strong>, then launch Concept from its new Home Screen icon.</span>
              </li>
            </ol>
            <button className="install-guide-done" onClick={() => setShowInstructions(false)} type="button">Got it</button>
          </section>
        </div>
      ), document.body)}
    </>
  );
}
